import { useMemo } from 'react';
import { useUserOptionRender } from '@src/hooks';
import {
  ENGAGEMENT_MODE_OPTIONS,
  INTEREST_OPTIONS,
  SOURCE_OPTIONS,
  LEAD_STATUS_OPTIONS,
  SOURCE_FILTER_OPTIONS,
} from '../constants';
import { getPhoneFieldConfig } from '@src/utils/phoneUtils';
import {
  formatOptionalPersonNameField,
  formatPersonNameField,
} from '@src/utils/nameUtils';
import dayjs from 'dayjs';

export const useLeadsFormFields = () => {
  const userOptionRender = useUserOptionRender({
    allowedUserTypes: ['doctor', 'therapist', 'counsellor'],
    showUserType: true,
    size: 'small'
  });

  return useMemo(
    () => [
      // Row 1: Patient Name (Left) | Mobile Number (Right)
      {
        name: 'patient_name',
        label: 'Patient Name',
        type: 'text' as const,
        required: true,
        placeholder: "Enter patient's full name",
        layout: { span: 12 }, // Left column
        props: {
          size: 'large',
          resourceAutoComplete: 'name',
        },
        tooltip: "Enter the patient's full name as it appears on their ID",
        transform: {
          output: (value: any) => formatPersonNameField(value),
        },
      },
      {
        name: 'patient_mobile_no',
        label: 'Mobile Number',
        ...getPhoneFieldConfig(true, '98765 43210'),
        layout: { span: 12 }, // Right column
        tooltip: 'Enter 10-digit mobile number (without country code)',
      },
      // Row 2: Interest (Left) | Source (Right)
      {
        name: 'interest',
        label: 'Interest',
        type: 'select' as const,
        required: true,
        placeholder: 'What is the lead interested in?',
        layout: { span: 12 }, // Left column
        options: [...INTEREST_OPTIONS],
        props: {
          size: 'large',
          mode: 'tags',
          showSearch: true,
          optionFilterProp: 'children',
          allowClear: true,
          maxTagCount: 'responsive',
          maxTagTextLength: 15,
        },
        tooltip: 'Select one or more treatments, or type and press Enter to add a new one.',
      },
      {
        name: 'source',
        label: 'Lead Source',
        type: 'select' as const,
        required: false,
        placeholder: 'Where did this lead come from?',
        layout: { span: 12 }, // Right column
        options: SOURCE_OPTIONS,
        props: {
          size: 'large',
          showSearch: true,
          optionFilterProp: 'children',
        },
        tooltip: 'Select where this lead discovered your clinic',
        onChange: (value: any, form: any) => {
          // Clear referred_by_name when source is not referral
          if (value !== 'REFERRAL') {
            form.setFieldsValue({ referred_by_name: null });
          }
        },
      },
      
      // Row 2.5: Referred By Name (conditional based on source)
      {
        name: 'referred_by_name',
        label: 'Referred By Name',
        type: 'text' as const,
        required: false,
        placeholder: 'Enter the name of person who referred',
        layout: { span: 12 }, // Left column
        props: {
          size: 'large',
          maxLength: 100,
        },
        tooltip: 'Enter the name of the person who referred this lead',
        transform: {
          output: (value: any) => formatOptionalPersonNameField(value),
        },
        hidden: true, // Hide by default - will be shown conditionally via form logic
      },
      // Row 3: Follow Up Date (Left) | Status (Right)
      // {
      //   name: 'follow_up_date',
      //   label: 'Follow Up Date & Time',
      //   type: 'datetime' as const,
      //   required: false,
      //   placeholder: 'When should you follow up?',
      //   layout: { span: 12 }, // Left column
      //   defaultValue: (() => {
      //     const now = dayjs();
      //     const minutes = now.minute();
      //     const roundedMinutes = Math.round(minutes / 15) * 15;
      //     let defaultTime = now.minute(roundedMinutes).second(0).millisecond(0);

      //     // If the rounded time is before 9 AM, set it to 9 AM today
      //     if (defaultTime.hour() < 9) {
      //       defaultTime = defaultTime.hour(9).minute(0);
      //     }
      //     // If the rounded time is after 8 PM, set it to 9 AM tomorrow
      //     else if (defaultTime.hour() >= 20) {
      //       defaultTime = defaultTime.add(1, 'day').hour(9).minute(0);
      //     }

      //     return defaultTime;
      //   })(),
      //   props: {
      //     size: 'large',
      //     showTime: true,
      //     showShortcuts: true,
      //     format: 'YYYY-MM-DD hh:mm A',
      //     showNow: true,
      //     showToday: true,
      //     minuteStep: 15,
      //     use12Hours: true,
      //     disabledDate: (current: any) => {
      //       return current && current < dayjs().startOf('day');
      //     },
      //     placeholder: 'Select date and time',
      //   },
      //   tooltip: 'Set a reminder for when to follow up with this lead',
      // },
      {
        name: 'status',
        label: 'Lead Status',
        type: 'select' as const,
        required: true,
        defaultValue: 'NEW',
        placeholder: "What's the current status?",
        layout: { span: 12 }, // Right column
        options: LEAD_STATUS_OPTIONS,
        props: {
          size: 'large',
          showSearch: true,
          optionFilterProp: 'children',
        },
        tooltip: 'Current status of this lead in your pipeline',
        onChange: (value: string, form: any) => {
          // Clear follow-up date when status changes to certain values
          if (['won', 'lost', 'cancelled'].includes(value)) {
            form.setFieldsValue({ follow_up_date: null });
          }
          // Clear conversion action fields when status changes
          if (!['CONVERTED', 'CLINIC_VISITED'].includes(value)) {
            form.setFieldsValue({ 
              is_create_patient: false,
              is_create_appointment: false 
            });
          }
        },
      },
      // Row 3.5: Create Patient option (conditional - shown when status is CONVERTED or CLINIC_VISITED)
      {
        name: 'is_create_patient',
        label: 'Create Patient',
        type: 'radio' as const,
        required: false,
        placeholder: 'Create patient from this lead?',
        layout: { span: 6 },
        options: [
          { label: 'Yes', value: true },
          { label: 'No', value: false },
        ],
        props: {
          size: 'large',
          buttonStyle: 'solid',
        },
        tooltip: 'Select to create a patient record when this lead is converted',
      },
      // Row 3.6: Create Appointment option (conditional - shown when status is CONVERTED or CLINIC_VISITED)
      {
        name: 'is_create_appointment',
        label: 'Create Appointment',
        type: 'radio' as const,
        required: false,
        placeholder: 'Create appointment for this lead?',
        layout: { span: 6 },
        options: [
          { label: 'Yes', value: true },
          { label: 'No', value: false },
        ],
        props: {
          size: 'large',
          buttonStyle: 'solid',
        },
        tooltip: 'Select to create an appointment when this lead is converted',
      },
      // Row 4: Assigned To (Left) | Priority (Right)
      {
        name: 'assigned_to',
        label: 'Assign To',
        type: 'resourceSelect' as const,
        required: true,
        placeholder: 'Who should handle this lead?',
        layout: { span: 12 }, // Left column
        props: {
          size: 'large',
          showSearch: true,
          optionFilterProp: 'children',
          loading: false,
          allowClear: true,
        },
        searchSelectProps: {
          resource: 'users',
          optionValue: 'id',
          searchFields: ['first_name', 'last_name'],
          searchDebounceMs: 300,
          pagination: { pageSize: 20 },
          filters: [
            {
              field: 'status',
              operator: 'eq',
              value: 'active',
            },
            {
              field: 'type',
              operator: 'in',
              value: ['doctor', 'therapist', 'counsellor'],
            },
          ],
          sorters: [
            {
              field: 'first_name',
              order: 'asc',
            },
          ],
          optionRender: userOptionRender,
          autoSelectFirst: true,
          queryOptions: {
            enabled: true,
            staleTime: 5 * 60 * 1000, // 5 minutes
            cacheTime: 10 * 60 * 1000, // 10 minutes
            refetchOnWindowFocus: false,
            refetchOnMount: true,
          },
        },
        tooltip: 'Assign this lead to a team member',
      },
      // Preserve existing patient link when converting lead (used for appointment creation)
      {
        name: 'patient_id',
        label: '',
        type: 'select' as const,
        required: false,
        layout: { span: 0 },
        props: {
          style: { display: 'none' },
        },
        hidden: true,
      },
      // Row 5: Notes (Full width)
      {
        name: 'notes',
        label: 'Notes & Comments',
        type: 'textarea' as const,
        required: false,
        placeholder: 'Add any important notes for lead...',
        layout: { span: 24 }, // Full width
        props: {
          rows: 4,
          showCount: true,
          maxLength: 500,
        },
      },
    ],
    [userOptionRender]
  );
};
